import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import './css/AdvancedSearch.css';
import SEO from './components/SEO';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function AdvancedSearch() {
  const navigate = useNavigate();
  const query = useQuery();
  
  // Default search parameters - use these for initial search
  const defaultSearchParams = {
    q: "",
    calories_min: "",
    calories_max: "",
    proteins_min: "",
    proteins_max: "",
    carbs_min: "",
    carbs_max: "",
    fats_min: "",
    fats_max: "",
    ingredient: "",
    vegan: false,
    gluten_free: false,
    lactose_free: false,
    vegetarian: false
  };
  
  const [searchParams, setSearchParams] = useState({
    q: query.get('q') || defaultSearchParams.q,
    calories_min: query.get('calories_min') || defaultSearchParams.calories_min,
    calories_max: query.get('calories_max') || defaultSearchParams.calories_max,
    proteins_min: query.get('proteins_min') || defaultSearchParams.proteins_min,
    proteins_max: query.get('proteins_max') || defaultSearchParams.proteins_max,
    carbs_min: query.get('carbs_min') || defaultSearchParams.carbs_min,
    carbs_max: query.get('carbs_max') || defaultSearchParams.carbs_max,
    fats_min: query.get('fats_min') || defaultSearchParams.fats_min,
    fats_max: query.get('fats_max') || defaultSearchParams.fats_max,
    ingredient: query.get('ingredient') || defaultSearchParams.ingredient,
    vegan: query.get('vegan') === 'true' || defaultSearchParams.vegan,
    gluten_free: query.get('gluten_free') === 'true' || defaultSearchParams.gluten_free,
    lactose_free: query.get('lactose_free') === 'true' || defaultSearchParams.lactose_free,
    vegetarian: query.get('vegetarian') === 'true' || defaultSearchParams.vegetarian
  });
  
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [initialSearchDone, setInitialSearchDone] = useState(false);
  
  // Add a constant for minimum loading time in milliseconds
  const MIN_LOADING_TIME = 800; // Show loading for at least 800ms
  
  // Active filters count
  const getActiveFiltersCount = () => {
    return Object.entries(searchParams).filter(([key, value]) => 
      (typeof value === 'boolean' ? value : value !== '' && value !== null && value !== undefined)
    ).length;
  };
  
  // Active filters list for display
  const getActiveFilters = () => {
    const filters = [];
    
    if (searchParams.q) filters.push({ key: 'q', label: `Keyword: ${searchParams.q}` });
    if (searchParams.ingredient) filters.push({ key: 'ingredient', label: `Ingredient: ${searchParams.ingredient}` });
    if (searchParams.calories_min) filters.push({ key: 'calories_min', label: `Min Calories: ${searchParams.calories_min}` });
    if (searchParams.calories_max) filters.push({ key: 'calories_max', label: `Max Calories: ${searchParams.calories_max}` });
    if (searchParams.proteins_min) filters.push({ key: 'proteins_min', label: `Min proteins: ${searchParams.proteins_min}g` });
    if (searchParams.proteins_max) filters.push({ key: 'proteins_max', label: `Max proteins: ${searchParams.proteins_max}g` });
    if (searchParams.carbs_min) filters.push({ key: 'carbs_min', label: `Min Carbs: ${searchParams.carbs_min}g` });
    if (searchParams.carbs_max) filters.push({ key: 'carbs_max', label: `Max Carbs: ${searchParams.carbs_max}g` });
    if (searchParams.fats_min) filters.push({ key: 'fats_min', label: `Min Fats: ${searchParams.fats_min}g` });
    if (searchParams.fats_max) filters.push({ key: 'fats_max', label: `Max Fats: ${searchParams.fats_max}g` });
    if (searchParams.vegan) filters.push({ key: 'vegan', label: 'Vegan' });
    if (searchParams.gluten_free) filters.push({ key: 'gluten_free', label: 'Gluten Free' });
    if (searchParams.lactose_free) filters.push({ key: 'lactose_free', label: 'Lactose Free' });
    if (searchParams.vegetarian) filters.push({ key: 'vegetarian', label: 'Vegetarian' });
    
    return filters;
  };
  
  // Remove individual filter
  const removeFilter = (key) => {
    setSearchParams(prev => ({
      ...prev,
      [key]: typeof prev[key] === 'boolean' ? false : ''
    }));
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle checkbox inputs
    if (type === 'checkbox') {
      setSearchParams(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      // Handle text and number inputs
      setSearchParams(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // If user presses Enter key in any input field, trigger search
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Add a separate keyPress handler for each input field
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent default form submission
      handleSearch();
    }
  };

  // Build query string from search params
  const buildQueryString = (params) => {
    const queryParams = new URLSearchParams();
    
    // Always include visibility=true
    queryParams.append('visibility', 'true');
    
    Object.entries(params).forEach(([key, value]) => {
      if (typeof value === 'boolean') {
        // Only include boolean values if they're true
        if (value) {
          queryParams.append(key, value);
        }
      } else if (value !== '' && value !== null && value !== undefined) {
        queryParams.append(key, value);
      }
    });
    
    return queryParams.toString();
  };

  // Handle search submission
  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    
    setLoading(true);
    setError(null);
    setPage(1);
    
    // Store the start time
    const startTime = Date.now();
    
    try {
      const queryString = buildQueryString(searchParams);
      const response = await fetch(`https://starfish-app-9uavi.ondigitalocean.app/recipes/search?${queryString}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Calculate elapsed time and remaining time to meet the minimum
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);
      
      // If we haven't met the minimum loading time, add a delay
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
      
      // Clear results before setting new ones
      setResults([]);
      
      // Ensure results is always an array
      if (Array.isArray(data)) {
        setResults(data);
        setHasMore(data.length === 10); // Assuming backend returns 10 items per page
      } else if (data && typeof data === 'object') {
        // If data is an object with results property
        if (Array.isArray(data.results)) {
          setResults(data.results);
          setHasMore(data.results.length === 10);
        } else {
          // Convert object to array if possible, or set empty array
          const resultsArray = data.recipes || Object.values(data) || [];
          setResults(Array.isArray(resultsArray) ? resultsArray : []);
          setHasMore(Array.isArray(resultsArray) && resultsArray.length === 10);
        }
      } else {
        setResults([]);
        setHasMore(false);
      }
      
      // Update URL with search parameters
      navigate(`/advanced-search?${queryString}`);
      
      // Set initial search as done
      setInitialSearchDone(true);
    } catch (error) {
      console.error('Error searching recipes:', error);
      setError('Failed to search recipes. Please try again.');
      setResults([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  // Load more results (for infinite scrolling)
  const loadMore = async () => {
    if (loading || !hasMore) return;
    
    const nextPage = page + 1;
    setLoading(true);
    
    // Store the start time
    const startTime = Date.now();
    
    try {
      const queryString = buildQueryString({
        ...searchParams,
        page: nextPage
      });
      
      const response = await fetch(`https://starfish-app-9uavi.ondigitalocean.app/recipes/search?${queryString}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Calculate elapsed time and remaining time to meet the minimum
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);
      
      // If we haven't met the minimum loading time, add a delay
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
      
      let newResults = [];
      if (Array.isArray(data)) {
        newResults = data;
      } else if (data && typeof data === 'object') {
        if (Array.isArray(data.results)) {
          newResults = data.results;
        } else {
          newResults = data.recipes || Object.values(data) || [];
          newResults = Array.isArray(newResults) ? newResults : [];
        }
      }
      
      if (newResults.length > 0) {
        setResults(prev => [...prev, ...newResults]);
        setPage(nextPage);
        setHasMore(newResults.length === 10);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more recipes:', error);
    } finally {
      setLoading(false);
    }
  };

  // Apply example filter presets
  const applyExampleFilter = (preset) => {
    let newParams = { ...searchParams };
    
    if (preset === 'calories') {
      newParams = {
        ...newParams,
        calories_min: '300',
        calories_max: '500'
      };
    } else if (preset === 'proteins') {
      newParams = {
        ...newParams,
        proteins_min: '30'
      };
    } else if (preset === 'lowcarb') {
      newParams = {
        ...newParams,
        carbs_max: '20',
        proteins_min: '25'
      };
    } else if (preset === 'vegan') {
      newParams = {
        ...newParams,
        vegan: true
      };
    } else if (preset === 'vegetarian') {
      newParams = {
        ...newParams,
        vegetarian: true
      };
    }
    
    setSearchParams(newParams);
  };

  // Handle back button click
  const handleGoBack = () => {
    navigate(-1);
  };
  
  // Perform initial search when component mounts
  useEffect(() => {
    const fetchInitialResults = async () => {
      if (!initialSearchDone) {
        // Check if URL already has search params
        const hasSearchParams = Array.from(query.entries()).length > 0;
        
        if (hasSearchParams) {
          // Use URL params for search
          handleSearch();
        } else {
          // Use default params for initial search
          try {
            setLoading(true);
            
            // Fetch some popular recipes or use a default search
            const response = await fetch(`https://starfish-app-9uavi.ondigitalocean.app/recipes?limit=10&visibility=true`);
            
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            // Handle different response formats
            if (Array.isArray(data)) {
              setResults(data);
            } else if (data && typeof data === 'object') {
              const resultsArray = data.recipes || Object.values(data) || [];
              setResults(Array.isArray(resultsArray) ? resultsArray : []);
            }
            
            setInitialSearchDone(true);
          } catch (error) {
            console.error('Error fetching initial recipes:', error);
            setError('Failed to load recipes. Please try searching for specific recipes.');
          } finally {
            setLoading(false);
          }
        }
      }
    };
    
    fetchInitialResults();
  }, []);

  return (
    <div className="advanced-search-page">
      <SEO 
        title="Advanced Recipe Search - Find Perfect Recipes | Savory"
        description="Use our advanced search to find recipes by calories, proteins, carbs, fats, ingredients, and dietary restrictions. Filter by vegan, vegetarian, gluten-free, and lactose-free options."
        keywords="advanced recipe search, filter recipes, dietary restrictions, vegan recipes, gluten free recipes, calorie search, protein search, ingredient search"
        url="/advanced-search"
        type="website"
        breadcrumb={[
          { name: "Home", url: "/" },
          { name: "Advanced Search", url: "/advanced-search" }
        ]}
      />
      {/* Back button */}
      <div className="back-button-container">
        <Link to="#" onClick={handleGoBack} className="back-button-details">
          <img src="/assets/icons/back-arrow.svg" alt="Back" className="backButtonImg" />
        </Link>
      </div>
      
      <div className="advanced-search-container">
        <motion.div 
          className="search-filters-panel"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1>Advanced Recipe Search</h1>
          
        
          
          <form onSubmit={(e) => { e.preventDefault(); handleSearch(); }} className="advanced-search-form">
            {/* Basic Info Section */}
            <div className="filter-section">
              
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <div className="search-row">
                  <div className="form-group full-width">
                    <label htmlFor="q">Recipe Name or Keywords</label>
                    <input
                      type="text"
                      id="q"
                      name="q"
                      value={searchParams.q}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder="Search by recipe name or keywords..."
                      className="search-input-advanced"
                    />
                  </div>
                </div>
                
                <div className="search-row">
                  <div className="form-group full-width">
                    <label htmlFor="ingredient">Ingredient</label>
                    <input
                      type="text"
                      id="ingredient"
                      name="ingredient"
                      value={searchParams.ingredient}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder="Search by ingredient..."
                      className="search-input-advanced"
                    />
                  </div>
                </div>
              </motion.div>
            </div>
            
            {/* Nutrition Section */}
            <div className="filter-section">
             
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <div className="search-row">
                  <div className="form-group">
                    <label htmlFor="calories_min">Calories (min)</label>
                    <input
                      type="number"
                      id="calories_min"
                      name="calories_min"
                      value={searchParams.calories_min}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder="Min"
                      className="search-input-advanced"
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="calories_max">Calories (max)</label>
                    <input
                      type="number"
                      id="calories_max"
                      name="calories_max"
                      value={searchParams.calories_max}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder="Max"
                      className="search-input-advanced"
                      min="0"
                    />
                  </div>
                </div>
                
                <div className="search-row">
                  <div className="form-group">
                    <label htmlFor="proteins_min">proteins (min g)</label>
                    <input
                      type="number"
                      id="proteins_min"
                      name="proteins_min"
                      value={searchParams.proteins_min}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder="Min"
                      className="search-input-advanced"
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="proteins_max">proteins (max g)</label>
                    <input
                      type="number"
                      id="proteins_max"
                      name="proteins_max"
                      value={searchParams.proteins_max}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder="Max"
                      className="search-input-advanced"
                      min="0"
                    />
                  </div>
                </div>
                
                <div className="search-row">
                  <div className="form-group">
                    <label htmlFor="carbs_min">Carbs (min g)</label>
                    <input
                      type="number"
                      id="carbs_min"
                      name="carbs_min"
                      value={searchParams.carbs_min}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder="Min"
                      className="search-input-advanced"
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="carbs_max">Carbs (max g)</label>
                    <input
                      type="number"
                      id="carbs_max"
                      name="carbs_max"
                      value={searchParams.carbs_max}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder="Max"
                      className="search-input-advanced"
                      min="0"
                    />
                  </div>
                </div>
                
                <div className="search-row">
                  <div className="form-group">
                    <label htmlFor="fats_min">Fats (min g)</label>
                    <input
                      type="number"
                      id="fats_min"
                      name="fats_min"
                      value={searchParams.fats_min}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder="Min"
                      className="search-input-advanced"
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="fats_max">Fats (max g)</label>
                    <input
                      type="number"
                      id="fats_max"
                      name="fats_max"
                      value={searchParams.fats_max}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder="Max"
                      className="search-input-advanced"
                      min="0"
                    />
                  </div>
                </div>
              </motion.div>
            </div>
            
            {/* Dietary Preferences Section */}
            <div className="filter-section">
              <h3 className="filter-section-title">Dietary Preferences</h3>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
                <div className="search-row dietary-preferences">
                  <div className="form-group checkbox-group">
                    <input
                      type="checkbox"
                      id="vegetarian"
                      name="vegetarian"
                      checked={searchParams.vegetarian}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      className="diet-checkbox"
                    />
                    <label htmlFor="vegetarian" className="checkbox-label">Vegetarian</label>
                  </div>
                  
                  <div className="form-group checkbox-group">
                    <input
                      type="checkbox"
                      id="vegan"
                      name="vegan"
                      checked={searchParams.vegan}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      className="diet-checkbox"
                    />
                    <label htmlFor="vegan" className="checkbox-label">Vegan</label>
                  </div>
                  
                  <div className="form-group checkbox-group">
                    <input
                      type="checkbox"
                      id="gluten_free"
                      name="gluten_free"
                      checked={searchParams.gluten_free}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      className="diet-checkbox"
                    />
                    <label htmlFor="gluten_free" className="checkbox-label">Gluten Free</label>
                  </div>
                  
                  <div className="form-group checkbox-group">
                    <input
                      type="checkbox"
                      id="lactose_free"
                      name="lactose_free"
                      checked={searchParams.lactose_free}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      className="diet-checkbox"
                    />
                    <label htmlFor="lactose_free" className="checkbox-label">Lactose Free</label>
                  </div>
                </div>
              </motion.div>
            </div>
            
            <div className="search-buttons">
              <motion.button
                type="submit"
                className="search-button-primary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Search Recipes
              </motion.button>
              
              <motion.button
                type="button"
                className="search-button-secondary"
                onClick={() => {
                  setSearchParams({
                    q: '',
                    calories_min: '',
                    calories_max: '',
                    proteins_min: '',
                    proteins_max: '',
                    carbs_min: '',
                    carbs_max: '',
                    fats_min: '',
                    fats_max: '',
                    ingredient: '',
                    vegan: false,
                    gluten_free: false,
                    lactose_free: false,
                    vegetarian: false
                  });
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Clear Filters
              </motion.button>
            </div>
            
            <div className="search-examples">
              <h3>Example Searches</h3>
              <div className="example-chips">
                <button 
                  type="button" 
                  className={`example-chip ${searchParams.calories_min === '300' && searchParams.calories_max === '500' ? 'active' : ''}`}
                  onClick={() => applyExampleFilter('calories')}
                >
                  300-500 calories
                </button>
                <button 
                  type="button" 
                  className={`example-chip ${searchParams.proteins_min === '30' ? 'active' : ''}`}
                  onClick={() => applyExampleFilter('proteins')}
                >
                  High proteins (30g+)
                </button>
                <button 
                  type="button" 
                  className={`example-chip ${searchParams.carbs_max === '20' && searchParams.proteins_min === '25' ? 'active' : ''}`}
                  onClick={() => applyExampleFilter('lowcarb')}
                >
                  Low carb, high proteins
                </button>
                <button 
                  type="button" 
                  className={`example-chip ${searchParams.vegan ? 'active' : ''}`}
                  onClick={() => applyExampleFilter('vegan')}
                >
                  Vegan recipes
                </button>
                <button 
                  type="button" 
                  className={`example-chip ${searchParams.vegetarian ? 'active' : ''}`}
                  onClick={() => applyExampleFilter('vegetarian')}
                >
                  Vegetarian recipes
                </button>
              </div>
            </div>
          </form>
        </motion.div>
        
        <motion.div 
          className="search-results-container"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="results-header">
            <h2>Search Results</h2>
            {results.length > 0 && (
              <span className="results-count">{results.length} recipes found</span>
            )}
          </div>
          
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          
          {loading && (
            <div className="loading-overlay">
              <div className="loading-container">
                <div className="loader"></div>
                <p>Searching for recipes...</p>
              </div>
            </div>
          )}
          
          {!loading && results.length === 0 && !error && (
            <div className="no-results">
              <h3>No recipes found</h3>
              <p>Try adjusting your search criteria.</p>
            </div>
          )}
          
          <div className="recipe-grid">
            <AnimatePresence>
              {results.map((recipe, index) => (
                <motion.div
                  key={recipe.id}
                  className="recipe-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <Link to={`/recipe-details/${recipe.id}`} className="recipe-link">
                    <div className="recipe-image-container">
                      <img 
                        src={recipe.url || 'https://via.placeholder.com/300x200?text=No+Image'} 
                        alt={recipe.name}
                        className="recipe-image"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/300x200?text=Image+Error';
                        }}
                      />
                    </div>
                    <div className="recipe-info">
                      <h3 className="recipe-title">{recipe.name}</h3>
                      <p className="recipe-description">
                        {recipe.description 
                          ? (recipe.description.length > 100 
                            ? recipe.description.substring(0, 100) + '...' 
                            : recipe.description)
                          : 'No description available'}
                      </p>
                      <div className="recipe-nutritional-info">
                        <div className="nutrition-badge calories">
                          <span>Calories</span>
                          <strong>{recipe.calories || 'N/A'}</strong>
                        </div>
                        <div className="nutrition-badge proteins">
                          <span>proteins</span>
                          <strong>{recipe.proteins || 'N/A'}g</strong>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
          
          {hasMore && results.length > 0 && (
            <div className="load-more-container">
              <motion.button
                className="load-more-button"
                onClick={loadMore}
                disabled={loading}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {loading ? 'Loading...' : 'Load More Recipes'}
              </motion.button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}

export default AdvancedSearch; 